import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { signupFormService } from '../../services/signupFormService';
import { SignupForm, FormType } from '../../types/signupForm';

const AdminSignupFormManager: React.FC = () => {
  const { currentUser } = useAuth();
  const { isDark } = useAdminTheme();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();

  const [forms, setForms] = useState<SignupForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<FormType | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'published' | 'draft'>('all');
  const [openFormMenu, setOpenFormMenu] = useState<string | null>(null);

  useEffect(() => {
    loadForms();
  }, []);

  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.form-card-menu')) {
        setOpenFormMenu(null);
      }
    };
    document.addEventListener('click', handleGlobalClick);
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  const loadForms = async () => {
    try {
      setLoading(true);
      // Only load admin-created forms
      const allForms = await signupFormService.getAllForms();
      const adminForms = allForms.filter(form => form.createdBy === 'admin');
      setForms(adminForms);
    } catch (error) {
      console.error('Error loading forms:', error);
      showError('Failed to load signup forms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = () => {
    navigate(`/admin/control-panel-7x9k2m/signup-forms/new/edit`);
  };

  const handleEditForm = (form: SignupForm) => {
    if (!form.id) return;
    navigate(`/admin/control-panel-7x9k2m/signup-forms/${form.id}/edit`);
  };

  const handleDeleteForm = async (form: SignupForm) => {
    if (!form.id) return;
    
    if (!window.confirm(`Are you sure you want to delete "${form.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await signupFormService.deleteForm(form.id);
      showSuccess('Form deleted successfully');
      await loadForms();
    } catch (error) {
      console.error('Error deleting form:', error);
      showError('Failed to delete form');
    }
  };

  const handleTogglePublish = async (form: SignupForm) => {
    if (!form.id) return;
    
    try {
      await signupFormService.publishForm(form.id, !form.isPublished);
      showSuccess(form.isPublished ? 'Form unpublished successfully' : 'Form published successfully');
      await loadForms();
    } catch (error) {
      console.error('Error toggling publish status:', error);
      showError('Failed to update form status');
    }
  };

  const getFormTypeLabel = (type: FormType): string => {
    const labels: Record<FormType, string> = {
      inline: 'Inline',
      popup: 'Popup',
      slidein: 'Slide-in',
      bar: 'Sticky Bar',
      embedded: 'Embedded'
    };
    return labels[type] || type;
  };

  const filteredForms = useMemo(() => {
    return forms.filter(form => {
      const matchesSearch = searchQuery === '' || 
        form.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (form.description && form.description.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesType = selectedType === 'all' || form.type === selectedType;
      
      const matchesStatus = selectedStatus === 'all' ||
        (selectedStatus === 'published' && form.isPublished) ||
        (selectedStatus === 'draft' && !form.isPublished);
      
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [forms, searchQuery, selectedType, selectedStatus]);

  const stats = useMemo(() => {
    return {
      total: forms.length,
      published: forms.filter(f => f.isPublished).length,
      draft: forms.filter(f => !f.isPublished).length,
      byType: forms.reduce((acc, form) => {
        acc[form.type] = (acc[form.type] || 0) + 1;
        return acc;
      }, {} as Record<FormType, number>)
    };
  }, [forms]);

  const surfaceCardClasses = isDark
    ? 'bg-gray-900/60 border border-gray-800/80 shadow-2xl backdrop-blur-xl'
    : 'bg-white/95 border border-gray-100 shadow-xl backdrop-blur';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
          isDark ? 'border-white' : 'border-gray-900'
        }`}></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className={`space-y-4 rounded-3xl p-6 ${surfaceCardClasses}`}>
        <div className="flex justify-between items-center">
          <div>
            <h2 className={`text-2xl font-bold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>Signup Form Manager</h2>
            <p className={`${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>Create and manage signup form templates that will be available to all users</p>
          </div>
          <button
            type="button"
            onClick={handleCreateForm}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors duration-200 text-center inline-block ${
              isDark 
                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                : 'bg-black hover:bg-gray-800 text-white'
            }`}
          >
            Create New Signup Form
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Forms</div>
            <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.total}</div>
          </div>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Published</div>
            <div className={`text-2xl font-bold text-green-600`}>{stats.published}</div>
          </div>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Drafts</div>
            <div className={`text-2xl font-bold text-gray-500`}>{stats.draft}</div>
          </div>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Submissions</div>
            <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {forms.reduce((sum, f) => sum + f.analytics.submissions, 0)}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search forms..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark 
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as FormType | 'all')}
            className={`px-4 py-2 rounded-lg border ${
              isDark 
                ? 'bg-gray-800 border-gray-700 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Types</option>
            <option value="inline">Inline</option>
            <option value="popup">Popup</option>
            <option value="slidein">Slide-in</option>
            <option value="bar">Sticky Bar</option>
            <option value="embedded">Embedded</option>
          </select>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value as 'all' | 'published' | 'draft')}
            className={`px-4 py-2 rounded-lg border ${
              isDark 
                ? 'bg-gray-800 border-gray-700 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {/* Forms Grid */}
      {filteredForms.length === 0 ? (
        <div className={`${surfaceCardClasses} rounded-3xl p-12 text-center`}>
          <div className="text-6xl mb-4">📝</div>
          <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {searchQuery || selectedType !== 'all' || selectedStatus !== 'all' 
              ? 'No templates match your filters' 
              : 'No signup form templates yet'}
          </h3>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
            {searchQuery || selectedType !== 'all' || selectedStatus !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Create your first signup form template that will be available to all users'}
          </p>
          {(!searchQuery && selectedType === 'all' && selectedStatus === 'all') && (
            <button
              onClick={handleCreateForm}
              className={`px-4 py-2 rounded-lg transition-colors ${
                isDark 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-black hover:bg-gray-800 text-white'
              }`}
            >
              Create New Signup Form
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {filteredForms.map((form) => (
            <div
              key={form.id}
              className={`group relative border transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                isDark ? 'bg-gray-900/80 border-gray-700' : 'bg-white border-gray-100'
              }`}
            >
              {/* Form Preview */}
              <div className={`relative h-64 overflow-hidden ${
                isDark ? 'bg-gray-950/90' : 'bg-gray-50'
              }`}>
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                  <div className="text-4xl mb-2">📋</div>
                  <div className={`text-sm font-semibold text-center ${
                    isDark ? 'text-gray-200' : 'text-gray-600'
                  }`}>
                    {form.name}
                  </div>
                  <div className={`text-xs mt-1 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {getFormTypeLabel(form.type)}
                  </div>
                </div>
                
                {/* Status Badge */}
                {form.isPublished && (
                  <div className="absolute top-2 left-2 z-10">
                    <span className="bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                      Published
                    </span>
                  </div>
                )}
              </div>

              {/* Form Info */}
              <div className="relative p-4 pb-3">
                <div className="flex items-center gap-2 pr-10">
                  <h3
                    className={`flex-1 text-sm font-semibold tracking-tight truncate ${
                      isDark ? 'text-gray-100' : 'text-gray-800'
                    }`}
                  >
                    {form.name}
                  </h3>
                </div>
                {form.description && (
                  <p className={`text-xs mt-1 line-clamp-2 ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {form.description}
                  </p>
                )}

                {/* Analytics */}
                <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Views</div>
                    <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {form.analytics.views}
                    </div>
                  </div>
                  <div>
                    <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Subs</div>
                    <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {form.analytics.submissions}
                    </div>
                  </div>
                  <div>
                    <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Rate</div>
                    <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {form.analytics.conversionRate.toFixed(0)}%
                    </div>
                  </div>
                </div>

                {/* Actions Menu */}
                <div className="absolute top-3 right-3 form-card-menu">
                  <div className="relative">
                    <button
                      type="button"
                      aria-label="Open form actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenFormMenu((prev) => (prev === form.id ? null : form.id));
                      }}
                      className="inline-flex h-8 w-8 items-center justify-center text-gray-400 hover:text-gray-900 dark:text-gray-500 dark:hover:text-gray-100 transition-colors"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <circle cx="12" cy="5" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="12" cy="19" r="1.5" />
                      </svg>
                    </button>
                    {openFormMenu === form.id && (
                      <div
                        className={`absolute right-0 bottom-full mb-2 w-44 rounded-xl border z-50 overflow-hidden ${
                          isDark
                            ? 'bg-gray-900 border-gray-700 text-gray-100 shadow-2xl'
                            : 'bg-white border-gray-200 text-gray-900 shadow-xl'
                        }`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                            isDark ? 'hover:bg-gray-800' : ''
                          }`}
                          onClick={() => {
                            setOpenFormMenu(null);
                            handleEditForm(form);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                            isDark ? 'hover:bg-gray-800' : ''
                          }`}
                          onClick={() => {
                            setOpenFormMenu(null);
                            handleTogglePublish(form);
                          }}
                        >
                          {form.isPublished ? 'Unpublish' : 'Publish'}
                        </button>
                        <button
                          type="button"
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-600 ${
                            isDark ? 'hover:bg-gray-800' : ''
                          }`}
                          onClick={() => {
                            setOpenFormMenu(null);
                            handleDeleteForm(form);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSignupFormManager;
